/**
 * Realty Hunter — Tenant Dashboard Page
 * Shows credit balance, search form (5 credits/search),
 * and 3 payment methods: Telegram / Stripe / USDT
 */

import React, { useState, useEffect } from 'react';
import {
  Target, Phone, Search, CreditCard, Bitcoin, Send,
  CheckCircle, XCircle, Clock, Loader2, Copy, ExternalLink,
  ChevronRight, Shield, Zap, AlertCircle, RefreshCw,
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ─── API helpers ─────────────────────────────────────────────────────────────
const authFetch = async (url, opts = {}) => {
  const res = await fetch(API_BASE + url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...opts.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Credit Balance Card ─────────────────────────────────────────────────────
const CreditBalanceCard = ({ balance, onRefresh, loading }) => (
  <div className="relative p-6 rounded-2xl bg-gradient-to-br from-gold-500/15 to-gold-500/5
    border border-gold-500/30 overflow-hidden">
    <div className="absolute top-0 right-0 w-40 h-40 bg-gold-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
    <div className="flex items-center justify-between mb-2">
      <p className="text-gray-400 text-sm font-medium">Your Hunter Credits</p>
      <button
        onClick={onRefresh}
        className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white transition-all"
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
    <div className="flex items-end gap-3">
      <p className="text-5xl font-black text-white">{loading ? '…' : balance}</p>
      <p className="text-gold-400 text-lg mb-1">credits</p>
    </div>
    <p className="text-gray-500 text-xs mt-2">
      1 search = 5 credits · Each credit never expires
    </p>
    {balance < 10 && !loading && (
      <div className="mt-3 flex items-center gap-2 text-amber-400 text-xs bg-amber-500/10
        border border-amber-500/20 rounded-lg px-3 py-2">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
        Low balance — top up to keep searching
      </div>
    )}
  </div>
);

// ─── Search Form ─────────────────────────────────────────────────────────────
const SearchForm = ({ balance, onResult, tenantId }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    if (balance < 5) {
      setError('Insufficient credits. Please top up first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await authFetch('/api/v1/hunter/search', {
        method: 'POST',
        body: JSON.stringify({ query_url: url, tenant_id: tenantId }),
      });
      onResult(result);
      setUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gold-500/20 flex items-center justify-center">
          <Search className="w-5 h-5 text-gold-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Find Property Owner</h3>
          <p className="text-gray-500 text-xs">Costs 5 credits per search</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-gray-400 text-xs mb-2 font-medium">
            Dubizzle or Bayut Listing URL
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.dubizzle.com/for-rent/properties/..."
            className="w-full bg-navy-800 border border-white/10 rounded-xl px-4 py-3
              text-white placeholder-gray-600 text-sm focus:outline-none focus:border-gold-500/50
              focus:bg-navy-700 transition-all"
            required
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10
            border border-red-500/20 rounded-lg px-3 py-2.5">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || balance < 5}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
            bg-gold-500 hover:bg-gold-400 disabled:bg-gray-700 disabled:cursor-not-allowed
            text-navy-900 font-bold transition-all disabled:text-gray-500"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</>
            : <><Target className="w-4 h-4" /> Find Owner (5 Credits)</>
          }
        </button>
      </form>
    </div>
  );
};

// ─── Result Card ─────────────────────────────────────────────────────────────
const ResultCard = ({ result }) => {
  const [copied, setCopied] = useState(false);
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!result) return null;

  const confColor = result.confidence >= 0.8
    ? 'text-green-400 bg-green-500/10 border-green-500/20'
    : result.confidence >= 0.5
    ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
    : 'text-red-400 bg-red-500/10 border-red-500/20';

  return (
    <div className="p-6 rounded-2xl bg-green-500/5 border border-green-500/20 animate-fade-in">
      <div className="flex items-center gap-3 mb-4">
        <CheckCircle className="w-6 h-6 text-green-400" />
        <h3 className="text-white font-semibold">Owner Found!</h3>
        <span className={`ml-auto text-xs px-2.5 py-1 rounded-full border ${confColor}`}>
          {Math.round((result.confidence || 0) * 100)}% confidence
        </span>
      </div>

      <div className="space-y-3">
        {result.owner_name && (
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
            <div>
              <p className="text-xs text-gray-500 mb-1">Owner Name</p>
              <p className="text-white font-medium">{result.owner_name}</p>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
          <div>
            <p className="text-xs text-gray-500 mb-1">Direct Phone / WhatsApp</p>
            <p className="text-white font-bold text-lg">{result.phone_number}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => copy(result.phone_number)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400
                hover:text-white transition-all"
              title="Copy number"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
            <a
              href={`https://wa.me/${result.phone_number.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400
                hover:text-green-300 transition-all"
              title="Open in WhatsApp"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Pricing / Buy Credits Modal ──────────────────────────────────────────────
const BuyCreditsPanel = () => {
  const [activeMethod, setActiveMethod] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [loading, setLoading] = useState(false);

  // USDT wallet address (set this to real address)
  const USDT_ADDRESS = 'TYourUSDTWalletAddressHere';

  const packages = [
    { credits: 10,  price: 29,  label: 'Starter' },
    { credits: 50,  price: 99,  label: 'Pro', popular: true },
    { credits: 200, price: 299, label: 'Agency' },
  ];

  const handleStripe = async (pkg) => {
    setLoading(true);
    try {
      const { checkout_url } = await authFetch('/api/v1/hunter/credits/stripe', {
        method: 'POST',
        body: JSON.stringify({ credits: pkg.credits, price: pkg.price }),
      });
      window.location.href = checkout_url;
    } catch (err) {
      alert('Stripe error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
          <CreditCard className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Buy Hunter Credits</h3>
          <p className="text-gray-500 text-xs">Credits never expire</p>
        </div>
      </div>

      {/* Package selector */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {packages.map((pkg) => (
          <button
            key={pkg.credits}
            onClick={() => setSelectedPkg(pkg)}
            className={`relative p-4 rounded-xl border text-center transition-all
              ${selectedPkg?.credits === pkg.credits
                ? 'bg-gold-500/15 border-gold-500/40'
                : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
          >
            {pkg.popular && (
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px]
                bg-gold-500 text-navy-900 rounded-full px-2 py-0.5 font-bold whitespace-nowrap">
                POPULAR
              </span>
            )}
            <p className="text-2xl font-black text-white">{pkg.credits}</p>
            <p className="text-gray-400 text-xs">credits</p>
            <p className="text-gold-400 font-bold mt-1">${pkg.price}</p>
          </button>
        ))}
      </div>

      {selectedPkg && (
        <div className="space-y-3">
          <p className="text-gray-400 text-xs text-center mb-2">
            Selected: <span className="text-white font-medium">{selectedPkg.credits} credits for ${selectedPkg.price}</span>
          </p>

          {/* Telegram */}
          <a
            href={`https://t.me/ArtinRealtyBot?start=buy_${selectedPkg.credits}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
              bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 text-sm font-medium
              border border-blue-500/20 transition-all"
          >
            <Send className="w-4 h-4" />
            Pay via Telegram Bot
          </a>

          {/* Stripe */}
          <button
            onClick={() => handleStripe(selectedPkg)}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
              bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-sm font-medium
              border border-purple-500/20 transition-all disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CreditCard className="w-4 h-4" />}
            Pay with Card (Stripe)
          </button>

          {/* USDT */}
          <button
            onClick={() => setActiveMethod(activeMethod === 'usdt' ? null : 'usdt')}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
              bg-green-500/20 hover:bg-green-500/30 text-green-300 text-sm font-medium
              border border-green-500/20 transition-all"
          >
            <Bitcoin className="w-4 h-4" /> Pay with USDT (TRC-20)
          </button>

          {activeMethod === 'usdt' && (
            <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20 text-center">
              <p className="text-xs text-gray-400 mb-3">Send exactly <strong className="text-white">${selectedPkg.price} USDT</strong> to:</p>
              <div className="flex items-center gap-2 bg-navy-800 rounded-lg p-2.5 mb-3">
                <code className="text-green-400 text-xs flex-1 text-left break-all">{USDT_ADDRESS}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(USDT_ADDRESS)}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <Copy className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              <p className="text-xs text-gray-500">
                After payment, send TX hash to{' '}
                <a href="https://t.me/ArtinRealtyBot" target="_blank" rel="noopener noreferrer"
                  className="text-blue-400 underline">@ArtinRealtyBot</a>
              </p>
            </div>
          )}
        </div>
      )}

      {!selectedPkg && (
        <p className="text-center text-gray-600 text-sm py-4">
          ☝️ Select a package above to see payment options
        </p>
      )}
    </div>
  );
};

// ─── Recent Searches Table ───────────────────────────────────────────────────
const SearchHistory = ({ searches, loading }) => {
  if (loading) return (
    <div className="flex justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
    </div>
  );
  if (!searches.length) return (
    <div className="text-center py-10 text-gray-600">
      <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm">No searches yet. Run your first search above.</p>
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {['Listing', 'Found Name', 'Phone', 'Confidence', 'Date'].map(h => (
              <th key={h} className="text-left py-3 px-4 text-gray-500 font-medium text-xs">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {searches.map((s) => (
            <tr key={s.id} className="hover:bg-white/3 transition-colors">
              <td className="py-3 px-4 text-gray-400 text-xs max-w-[200px] truncate">
                <a href={s.query_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-white">
                  {new URL(s.query_url).hostname.replace('www.','')}
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </td>
              <td className="py-3 px-4 text-white">{s.result_name || '—'}</td>
              <td className="py-3 px-4 font-mono text-green-400">{s.result_phone || '—'}</td>
              <td className="py-3 px-4">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  (s.confidence || 0) >= 0.8 ? 'bg-green-500/20 text-green-400'
                  : (s.confidence || 0) >= 0.5 ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
                }`}>
                  {Math.round((s.confidence || 0) * 100)}%
                </span>
              </td>
              <td className="py-3 px-4 text-gray-500 text-xs">
                {new Date(s.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN HUNTER PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function HunterPage ({ tenantId }) {
  const [credits,  setCredits]  = useState(0);
  const [searches, setSearches] = useState([]);
  const [latestResult, setLatestResult] = useState(null);
  const [loadingCredits,  setLoadingCredits]  = useState(true);
  const [loadingSearches, setLoadingSearches] = useState(true);
  const [activeTab, setActiveTab] = useState('search'); // 'search' | 'history' | 'buy'

  const fetchCredits = async () => {
    setLoadingCredits(true);
    try {
      const data = await authFetch(`/api/v1/hunter/credits?tenant_id=${tenantId}`);
      setCredits(data.balance ?? 0);
    } catch {
      setCredits(0);
    } finally {
      setLoadingCredits(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingSearches(true);
    try {
      const data = await authFetch(`/api/v1/hunter/history?tenant_id=${tenantId}`);
      setSearches(Array.isArray(data) ? data : (data.searches || []));
    } catch {
      setSearches([]);
    } finally {
      setLoadingSearches(false);
    }
  };

  useEffect(() => {
    if (!tenantId || tenantId === 0) return;
    fetchCredits();
    fetchHistory();
  }, [tenantId]);

  const handleSearchResult = (result) => {
    setLatestResult(result);
    fetchCredits();  // Deduct 5 credits
    fetchHistory();  // Add to history
    setActiveTab('search');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gold-500/30 to-gold-500/10
          border border-gold-500/20 flex items-center justify-center">
          <Target className="w-7 h-7 text-gold-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Realty Hunter</h1>
          <p className="text-gray-400 text-sm">Find direct owner contacts from any Dubai property listing</p>
        </div>
        <div className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl
          bg-gold-500/10 border border-gold-500/20">
          <Zap className="w-4 h-4 text-gold-400" />
          <span className="text-white font-bold">{loadingCredits ? '…' : credits}</span>
          <span className="text-gray-400 text-sm">credits</span>
        </div>
      </div>

      {/* How it works banner */}
      <div className="flex items-center gap-4 mb-8 p-4 rounded-2xl bg-blue-500/5 border border-blue-500/15">
        {[
          { n: '1', t: 'Paste listing URL' },
          { n: '→', t: '' },
          { n: '2', t: 'AI cross-references data' },
          { n: '→', t: '' },
          { n: '3', t: 'Get owner\'s WhatsApp' },
        ].map((s, i) => s.n === '→'
          ? <ChevronRight key={i} className="w-4 h-4 text-gray-600 flex-shrink-0" />
          : (
            <div key={i} className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400
                text-xs font-bold flex items-center justify-center">{s.n}</span>
              <span className="text-gray-400 text-sm">{s.t}</span>
            </div>
          )
        )}
        <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
          <Shield className="w-3.5 h-3.5" /> Avg 3 minutes
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 p-1 bg-navy-800/60 rounded-xl mb-6 w-fit">
        {[
          { id: 'search',  label: '🔍 Search' },
          { id: 'history', label: '📋 History' },
          { id: 'buy',     label: '🪙 Buy Credits' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all
              ${activeTab === tab.id
                ? 'bg-gold-500 text-navy-900'
                : 'text-gray-400 hover:text-white'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'search' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <CreditBalanceCard
              balance={credits}
              onRefresh={fetchCredits}
              loading={loadingCredits}
            />
            <SearchForm
              balance={credits}
              onResult={handleSearchResult}
              tenantId={tenantId}
            />
          </div>
          <div>
            {latestResult
              ? <ResultCard result={latestResult} />
              : (
                <div className="h-full flex flex-col items-center justify-center
                  rounded-2xl border border-dashed border-white/10 p-12 text-center">
                  <Target className="w-16 h-16 text-gray-700 mb-4" />
                  <p className="text-gray-500">Enter a listing URL and hit search</p>
                  <p className="text-gray-700 text-xs mt-1">Owner details will appear here</p>
                </div>
              )
            }
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h3 className="text-white font-medium mb-4">Recent Searches</h3>
          <SearchHistory searches={searches} loading={loadingSearches} />
        </div>
      )}

      {activeTab === 'buy' && (
        <div className="max-w-lg">
          <BuyCreditsPanel />
        </div>
      )}
    </div>
  );
}
